import { useState } from 'react';
import { Tabs as MuiTabs, Tab as MuiTab, Box } from '@mui/material';
import { TabsProps } from './Tabs.props';
import { TabPanel } from './TabPanel';

export const Tabs = ({
  tabs,
  value: controlledValue,
  defaultValue,
  onChange,
  iconPosition = 'top',
  centered = false,
  indicatorColor = 'primary',
  textColor = 'primary',
  variant = 'standard',
  tabPanelProps,
  ...props
}: TabsProps) => {
  // Support both controlled and uncontrolled modes
  const [internalValue, setInternalValue] = useState<string>(
    defaultValue || tabs[0]?.value || ''
  );

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  // Check if any tabs have content to render TabPanels
  const hasContent = tabs.some((tab) => tab.content !== undefined);

  // Determine if we should use standard MUI color props or custom colors via sx
  const isStandardIndicatorColor =
    indicatorColor === 'primary' || indicatorColor === 'secondary';
  const isStandardTextColor =
    textColor === 'primary' ||
    textColor === 'secondary' ||
    textColor === 'inherit';

  // Build sx prop for custom colors
  const customSx = {
    ...(!isStandardIndicatorColor && {
      '& .MuiTabs-indicator': {
        backgroundColor: indicatorColor,
      },
    }),
    ...(!isStandardTextColor && {
      '& .MuiTab-root': {
        color: textColor,
      },
      '& .Mui-selected': {
        color: textColor,
      },
    }),
    ...props.sx,
  };

  return (
    <Box sx={{ width: '100%' }}>
      <MuiTabs
        value={currentValue}
        onChange={handleChange}
        indicatorColor={isStandardIndicatorColor ? indicatorColor : undefined}
        textColor={isStandardTextColor ? textColor : undefined}
        variant={variant}
        centered={centered}
        scrollButtons="auto"
        allowScrollButtonsMobile
        {...props}
        sx={customSx}
      >
        {tabs.map((tab) => (
          <MuiTab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            icon={tab.icon}
            iconPosition={iconPosition}
            disabled={tab.disabled}
            id={`tab-${tab.value}`}
            aria-controls={`tabpanel-${tab.value}`}
            {...tab.tabProps}
          />
        ))}
      </MuiTabs>

      {/* Render TabPanels if content is provided */}
      {hasContent &&
        tabs.map((tab) => (
          <TabPanel
            key={tab.value}
            value={tab.value}
            activeValue={currentValue}
            {...tabPanelProps}
          >
            {tab.content}
          </TabPanel>
        ))}
    </Box>
  );
};

export default Tabs;
